import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scroll behaviour on route changes:
 *  - PUSH/REPLACE (clicking a link/button) → jump to top.
 *  - POP (browser back/forward) → restore the scroll we recorded last time
 *    that history entry was visible, so returning to a long feed lands the
 *    user where they left off.
 *  - hash navigation (#section) → leave it to the browser.
 *  - opt-out via `location.state.preserveScroll`.
 */

// In-memory map of history-entry key → scrollY. Lives for the tab session;
// sessionStorage isn't needed because the browser already handles full
// reloads via its native restoration.
const scrollPositions = new Map<string, number>();

export function ScrollToTop() {
  const { pathname, hash, state, key } = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const prevKeyRef = useRef<string>(key);

  // Disable the browser's own scroll restoration so ours is authoritative.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => { window.history.scrollRestoration = prev; };
    }
  }, []);

  // Save the outgoing entry's scroll position before applying the new one.
  useEffect(() => {
    const prevKey = prevKeyRef.current;
    if (prevKey && prevKey !== key) {
      scrollPositions.set(prevKey, window.scrollY);
    }
    prevKeyRef.current = key;

    if ((state as { preserveScroll?: boolean } | null)?.preserveScroll) return;
    if (hash) return;

    if (navType === "POP") {
      // Back/forward — restore where we were on this history entry.
      const saved = scrollPositions.get(key) ?? 0;
      // Wait for the new route's content to paint before restoring.
      requestAnimationFrame(() => {
        window.scrollTo({ top: saved, left: 0, behavior: "auto" });
      });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [key, pathname, hash, state, navType]);

  return null;
}